import { action } from "typesafe-actions";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import {
  IRestrictedModule,
  ISubscriptionPlan,
  ISubscriptionPlanFeature,
  ISubscriptionPriorityList,
  IUserSubscription,
} from "./subscription.types";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";

import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { uploadFile } from "../../components/FileUpload/utils";

// Action Types
export const FETCH_SUBSCRIPTION_PLAN_LIST_PROGRESS =
  "FETCH_SUBSCRIPTION_PLAN_LIST_PROGRESS";
export const FETCH_SUBSCRIPTION_PLAN_LIST_SUCCESS =
  "FETCH_SUBSCRIPTION_PLAN_LIST_SUCCESS";
export const FETCH_SUBSCRIPTION_PLAN_LIST_FAILED =
  "FETCH_SUBSCRIPTION_PLAN_LIST_FAILED";

export const FETCH_SUBSCRIPTION_PLAN_PROGRESS =
  "FETCH_SUBSCRIPTION_PLAN_PROGRESS";
export const FETCH_SUBSCRIPTION_PLAN_SUCCESS =
  "FETCH_SUBSCRIPTION_PLAN_SUCCESS";
export const FETCH_SUBSCRIPTION_PLAN_FAILED = "FETCH_SUBSCRIPTION_PLAN_FAILED";

// Action Creators
export const fetchSubscriptionPlanListProgress = () =>
  action(FETCH_SUBSCRIPTION_PLAN_LIST_PROGRESS);
export const fetchSubscriptionPlanListSuccess = (data: ISubscriptionPlan[]) =>
  action(FETCH_SUBSCRIPTION_PLAN_LIST_SUCCESS, { data });
export const fetchSubscriptionPlanListFailed = () =>
  action(FETCH_SUBSCRIPTION_PLAN_LIST_FAILED);

export const fetchSubscriptionPlanProgress = () =>
  action(FETCH_SUBSCRIPTION_PLAN_PROGRESS);
export const fetchSubscriptionPlanSuccess = (data: ISubscriptionPlan) =>
  action(FETCH_SUBSCRIPTION_PLAN_SUCCESS, { data });
export const fetchSubscriptionPlanFailed = () =>
  action(FETCH_SUBSCRIPTION_PLAN_FAILED);

// Async Actions
export const fetchSubscriptionPlanListAsync =
  (validity_unit: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchSubscriptionPlanListProgress());
      const res = await api.get(
        `/subscription/get-subscription-plan?validity_unit=${validity_unit}`
      );
      const data: ISubscriptionPlan[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchSubscriptionPlanListSuccess(data));
    } catch (err: any) {
      dispatch(fetchSubscriptionPlanListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const fetchSubscriptionPlanAsync =
  (
    subscriptionPlanUuid: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchSubscriptionPlanProgress());
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/subscription/get-subscription-plan?subscription_plan_uuid=${subscriptionPlanUuid}`
      );
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchSubscriptionPlanSuccess(data[0]));
      } else {
        dispatch(fetchSubscriptionPlanFailed());
      }
    } catch (err: any) {
      dispatch(fetchSubscriptionPlanFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertSubscriptionPlanAsync =
  (
    subscriptionPlan: ISubscriptionPlan,
    onCallback: (isSuccess?: boolean, data?: ISubscriptionPlan) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const {
        created_by_uuid,
        created_by_name,
        modified_by_uuid,
        modified_by_name,
        create_ts,
        insert_ts,
        subscription_plan_features,
        ...rest
      } = subscriptionPlan;
      const res = await api.post("/subscription/upsert-subscription-plan", {
        ...rest,
      });
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallback(true, data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// User Subscription List Actions
export const FETCH_USER_SUBSCRIPTION_LIST_PROGRESS =
  "FETCH_USER_SUBSCRIPTION_LIST_PROGRESS";
export const FETCH_USER_SUBSCRIPTION_LIST_SUCCESS =
  "FETCH_USER_SUBSCRIPTION_LIST_SUCCESS";
export const FETCH_USER_SUBSCRIPTION_LIST_FAILED =
  "FETCH_USER_SUBSCRIPTION_LIST_FAILED";

export const fetchUserSubscriptionListProgress = () =>
  action(FETCH_USER_SUBSCRIPTION_LIST_PROGRESS);
export const fetchUserSubscriptionListSuccess = (data: IUserSubscription[]) =>
  action(FETCH_USER_SUBSCRIPTION_LIST_SUCCESS, { data });
export const fetchUserSubscriptionListFailed = () =>
  action(FETCH_USER_SUBSCRIPTION_LIST_FAILED);

export const fetchUserSubscriptionListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date?: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,

    searchByName?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let searchQuery = "";

      if ((searchValue?.length ?? 0) > 0 || searchByName) {
        const advanceFilter = [...(searchValue || [])]; // Ensure it's always an array

        if (searchByName) {
          advanceFilter.push({
            column: ["user_subscription_uuid"],
            value: searchByName,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      if (date) {
        searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
      }

      dispatch(fetchUserSubscriptionListProgress());
      const res = await api.get(
        `/subscription/get-user-subscription?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}` +
          searchQuery
      );
      const data: IUserSubscription[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchUserSubscriptionListSuccess(data));
    } catch (err: any) {
      dispatch(fetchUserSubscriptionListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// User Subscription Actions
export const FETCH_RESTRICTED_MODULE_PROGRESS =
  "FETCH_RESTRICTED_MODULE_PROGRESS";
export const FETCH_RESTRICTED_MODULE_SUCCESS =
  "FETCH_RESTRICTED_MODULE_SUCCESS";
export const FETCH_RESTRICTED_MODULE_FAILED = "FETCH_RESTRICTED_MODULE_FAILED";

export const fetchRestrictedModuleProgress = () =>
  action(FETCH_RESTRICTED_MODULE_PROGRESS);
export const fetchRestrictedModuleSuccess = (data: IRestrictedModule) =>
  action(FETCH_RESTRICTED_MODULE_SUCCESS, { data });
export const fetchRestrictedModuleFailed = () =>
  action(FETCH_RESTRICTED_MODULE_FAILED);

export const fetchRestrictedModuleAsync =
  (
    restrictedModuleUuid: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchRestrictedModuleProgress());
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/subscription/get-restricted-module?restricted_module_uuid=${restrictedModuleUuid}`
      );
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchRestrictedModuleSuccess(data[0]));
      } else {
        dispatch(fetchRestrictedModuleFailed());
      }
    } catch (err: any) {
      dispatch(fetchRestrictedModuleFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertRestrictedModuleAsync =
  (
    restrictedModule: IRestrictedModule,
    onCallback: (isSuccess?: boolean, data?: IRestrictedModule) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const { create_ts, insert_ts, created_by_uuid, ...rest } =
        restrictedModule;
      const res = await api.post("/subscription/upsert-restricted-module", {
        ...rest,
      });
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallback(true, data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_USER_SUBSCRIPTION_PROGRESS =
  "FETCH_USER_SUBSCRIPTION_PROGRESS";
export const FETCH_USER_SUBSCRIPTION_SUCCESS =
  "FETCH_USER_SUBSCRIPTION_SUCCESS";
export const FETCH_USER_SUBSCRIPTION_FAILED = "FETCH_USER_SUBSCRIPTION_FAILED";

export const fetchUserSubscriptionProgress = () =>
  action(FETCH_USER_SUBSCRIPTION_PROGRESS);
export const fetchUserSubscriptionSuccess = (data: IUserSubscription) =>
  action(FETCH_USER_SUBSCRIPTION_SUCCESS, { data });
export const fetchUserSubscriptionFailed = () =>
  action(FETCH_USER_SUBSCRIPTION_FAILED);

export const fetchUserSubscriptionAsync =
  (
    userSubscriptionUuid: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchUserSubscriptionProgress());
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/subscription/get-user-subscription?user_subscription_uuid=${userSubscriptionUuid}`
      );
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchUserSubscriptionSuccess(data[0]));
      } else {
        dispatch(fetchUserSubscriptionFailed());
      }
    } catch (err: any) {
      dispatch(fetchUserSubscriptionFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertUserSubscriptionAsync =
  (
    userSubscription: IUserSubscription,
    onCallback: (isSuccess?: boolean, data?: IUserSubscription) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const { create_ts, insert_ts, created_by_uuid, ...rest } =
        userSubscription;
      const res = await api.post("/subscription/upsert-user-subscription", {
        ...rest,
      });
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallback(true, data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertPriorityListAsync =
  (
    subscriptionPriorityList: ISubscriptionPriorityList,
    onCallback: (isSuccess?: boolean, data?: ISubscriptionPriorityList) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const res = await api.post("/general/upsert-priority-list", {
        ...subscriptionPriorityList,
      });
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallback(true, data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Action Types

export const FETCH_SUBSCRIPTION_PLAN_FEATURE_PROGRESS =
  "FETCH_SUBSCRIPTION_PLAN_FEATURE_PROGRESS";
export const FETCH_SUBSCRIPTION_PLAN_FEATURE_SUCCESS =
  "FETCH_SUBSCRIPTION_PLAN_FEATURE_SUCCESS";
export const FETCH_SUBSCRIPTION_PLAN_FEATURE_FAILED =
  "FETCH_SUBSCRIPTION_PLAN_FEATURE_FAILED";

// Action Creators

export const fetchSubscriptionPlanFeatureProgress = () =>
  action(FETCH_SUBSCRIPTION_PLAN_FEATURE_PROGRESS);
export const fetchSubscriptionPlanFeatureSuccess = (
  data: ISubscriptionPlanFeature
) => action(FETCH_SUBSCRIPTION_PLAN_FEATURE_SUCCESS, { data });
export const fetchSubscriptionPlanFeatureFailed = () =>
  action(FETCH_SUBSCRIPTION_PLAN_FEATURE_FAILED);

export const fetchSubscriptionPlanFeatureAsync =
  (
    subscriptionPlanUuid: string,
    onCallback: (isSuccess?: boolean, data?: ISubscriptionPlanFeature[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchSubscriptionPlanFeatureProgress());
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/subscription/get-subscription-plan-feature?subscription_plan_uuid=${subscriptionPlanUuid}`
      );
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchSubscriptionPlanFeatureSuccess(data));
        onCallback(true, data);
      } else {
        dispatch(fetchSubscriptionPlanFeatureFailed());
        onCallback(false);
      }
    } catch (err: any) {
      dispatch(fetchSubscriptionPlanFeatureFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertSubscriptionPlanFeatureAsync =
  (
    subscriptionPlanFeature: ISubscriptionPlanFeature,
    onCallback: (isSuccess?: boolean, data?: ISubscriptionPlanFeature) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const res = await api.post(
        "/subscription/upsert-subscription-plan-feature",
        {
          ...subscriptionPlanFeature,
        }
      );
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallback(true, data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Clear Subscription Plan State
export const CLEAR_SUBSCRIPTION_PLAN_STATE = "CLEAR_SUBSCRIPTION_PLAN_STATE";
export const CLEAR_SUBSCRIPTION_PLAN = "CLEAR_SUBSCRIPTION_PLAN";

export const clearSubscriptionPlanState = () =>
  action(CLEAR_SUBSCRIPTION_PLAN_STATE);
export const clearSubscriptionPlan = () => action(CLEAR_SUBSCRIPTION_PLAN);

// Clear User Subscription State

export const CLEAR_USER_SUBSCRIPTION_STATE = "CLEAR_USER_SUBSCRIPTION_STATE";
export const CLEAR_USER_SUBSCRIPTION = "CLEAR_USER_SUBSCRIPTION";

export const clearUserSubscriptionState = () =>
  action(CLEAR_USER_SUBSCRIPTION_STATE);
export const clearUserSubscription = () => action(CLEAR_USER_SUBSCRIPTION);

// Clear Restricted Module State
export const CLEAR_RESTRICTED_MODULE_STATE = "CLEAR_RESTRICTED_MODULE_STATE";
export const CLEAR_RESTRICTED_MODULE = "CLEAR_RESTRICTED_MODULE";

export const clearRestrictedModuleState = () =>
  action(CLEAR_RESTRICTED_MODULE_STATE);
export const clearRestrictedModule = () => action(CLEAR_RESTRICTED_MODULE);

// Copy Subscription Plan Actions
export const COPY_SUBSCRIPTION_PLAN_PROGRESS =
  "COPY_SUBSCRIPTION_PLAN_PROGRESS";
export const COPY_SUBSCRIPTION_PLAN_SUCCESS = "COPY_SUBSCRIPTION_PLAN_SUCCESS";
export const COPY_SUBSCRIPTION_PLAN_FAILED = "COPY_SUBSCRIPTION_PLAN_FAILED";

export const copySubscriptionPlanProgress = () =>
  action(COPY_SUBSCRIPTION_PLAN_PROGRESS);
export const copySubscriptionPlanSuccess = () =>
  action(COPY_SUBSCRIPTION_PLAN_SUCCESS);
export const copySubscriptionPlanFailed = () =>
  action(COPY_SUBSCRIPTION_PLAN_FAILED);

export const copySubscriptionPlanAsync =
  (
    subscription_plan_uuid: string,
    onCallback?: (isSuccess?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      dispatch(copySubscriptionPlanProgress());
      const res = await api.post(`/subscription/copy-subscription-plan`, {
        subscription_plan_uuid,
      });
      dispatch(copySubscriptionPlanSuccess());
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message || "Subscription plan copied successfully",
          displayAs: "snackbar",
        })
      );
      onCallback?.(true);
    } catch (err: any) {
      dispatch(copySubscriptionPlanFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to copy subscription plan",
          displayAs: "snackbar",
        })
      );
      onCallback?.(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
